// Monophonic pitch detection for the tuner (F9.2): YIN (de Cheveigné & Kawahara, 2002) with
// parabolic interpolation for sub-sample periods.

export interface PitchReading {
  /** Hz. */
  frequency: number;
  /** 0–1: how periodic the signal is (1 − YIN's normalized difference at the period). */
  clarity: number;
}

export interface PitchOptions {
  minHz: number;
  maxHz: number;
  /** YIN threshold: the first dip below it is taken as the period. */
  threshold: number;
  /** Below this RMS the input counts as silence. */
  minRms: number;
}

const DEFAULTS: PitchOptions = { minHz: 40, maxHz: 1500, threshold: 0.12, minRms: 0.005 };

export function detectPitch(
  buffer: Float32Array,
  sampleRate: number,
  options: Partial<PitchOptions> = {},
): PitchReading | null {
  const o = { ...DEFAULTS, ...options };
  const maxTau = Math.min(Math.floor(sampleRate / o.minHz), Math.floor(buffer.length / 2));
  const minTau = Math.max(2, Math.floor(sampleRate / o.maxHz));
  if (maxTau <= minTau + 2) return null;
  const width = buffer.length - maxTau;

  let energy = 0;
  for (const v of buffer) energy += v * v;
  if (Math.sqrt(energy / buffer.length) < o.minRms) return null;

  // Cumulative mean normalized difference function.
  const cmnd = new Float64Array(maxTau + 1);
  cmnd[0] = 1;
  let running = 0;
  for (let tau = 1; tau <= maxTau; tau++) {
    let sum = 0;
    for (let j = 0; j < width; j++) {
      const d = buffer[j]! - buffer[j + tau]!;
      sum += d * d;
    }
    running += sum;
    cmnd[tau] = running > 0 ? (sum * tau) / running : 1;
  }

  // First dip under the threshold, followed down to its minimum; else the global minimum.
  let tau = -1;
  for (let t = minTau; t < maxTau; t++) {
    if (cmnd[t]! < o.threshold) {
      while (t + 1 < maxTau && cmnd[t + 1]! < cmnd[t]!) t++;
      tau = t;
      break;
    }
  }
  if (tau < 0) {
    let best = minTau;
    for (let t = minTau + 1; t < maxTau; t++) if (cmnd[t]! < cmnd[best]!) best = t;
    if (cmnd[best]! > 0.35) return null;
    tau = best;
  }

  // Parabola through the dip and its neighbours gives the period between samples.
  const a = cmnd[tau - 1]!;
  const b = cmnd[tau]!;
  const c = cmnd[tau + 1]!;
  const denom = a - 2 * b + c;
  const shift = denom > 0 ? (0.5 * (a - c)) / denom : 0;
  return { frequency: sampleRate / (tau + shift), clarity: Math.max(0, Math.min(1, 1 - b)) };
}

export interface NoteReading {
  /** MIDI note number of the nearest note. */
  midi: number;
  /** Distance from that note, −50…+50 cents. */
  cents: number;
}

/** Nearest equal-tempered note to a frequency (A4 = `a4` Hz). */
export function nearestNote(frequency: number, a4 = 440): NoteReading {
  const exact = 69 + 12 * Math.log2(frequency / a4);
  const midi = Math.round(exact);
  return { midi, cents: (exact - midi) * 100 };
}

/** Cents from `reference` Hz to `frequency` Hz. */
export function centsBetween(frequency: number, reference: number): number {
  return 1200 * Math.log2(frequency / reference);
}
